import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, AlertCircle, FileQuestion, CheckCircle2, Calendar, Inbox, ChevronRight, Clock } from 'lucide-react';
import { format, isPast, isToday, differenceInCalendarDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/Badge';
import { ETAPA_MAP } from '../lib/constants';
import { parseDate, formatPeriodo, duracaoTexto, acaoAcontecendoAgora } from '../lib/dates';
import { PrioridadeBadge } from '../components/demandas/PrioridadeBadge';

// Dashboard do secretário: foco em "o que eu preciso fazer" e "o que está
// acontecendo nas minhas demandas". Sem Kanban, sem top secretarias, sem
// tempo médio por etapa — esses são pro pessoal da Comunicação.
export function SecretarioDashboardPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [painel, setPainel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/demandas/painel-secretario')
      .then(setPainel)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loader-full"><span className="spinner" /></div>;
  if (!painel) return <div className="page">Erro ao carregar painel.</div>;

  const contadores = Object.fromEntries(painel.contadores.map((c) => [c.etapa, c.total]));
  const emAndamento = Object.entries(contadores)
    .filter(([k]) => !['publicado', 'cancelada'].includes(k))
    .reduce((acc, [, v]) => acc + v, 0);
  const aguardandoVoce = painel.pedidos_pendentes.length + painel.aprovacoes_pendentes.length;
  const publicadas = contadores.publicado || 0;
  const total = Object.values(contadores).reduce((a, b) => a + b, 0);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Olá, {user?.nome?.split(' ')[0]} 👋</h1>
          <p className="page-subtitle">Acompanhe suas demandas e responda o que está pendente.</p>
        </div>
        <button className="btn btn-primary" onClick={() => nav('/nova-demanda')}>
          <Plus size={18} /> Nova Demanda
        </button>
      </div>

      <div className="stat-cards" style={{ marginBottom: 20, gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <button type="button" className="stat-card" onClick={() => nav('/minhas')} style={{ borderLeftColor: '#0369a1' }}>
          <div className="stat-label"><Inbox size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Em andamento</div>
          <div className="stat-value">{emAndamento}</div>
          <div className="text-xs muted">Em produção pela Comunicação</div>
        </button>
        <button
          type="button"
          className="stat-card"
          onClick={() => document.getElementById('pendencias')?.scrollIntoView({ behavior: 'smooth' })}
          style={{ borderLeftColor: aguardandoVoce ? '#dc2626' : '#16a34a' }}
        >
          <div className="stat-label">
            {aguardandoVoce ? <AlertCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <CheckCircle2 size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
            Aguardando você
          </div>
          <div className="stat-value">{aguardandoVoce}</div>
          <div className="text-xs muted">{aguardandoVoce ? 'Há itens pendentes de resposta' : 'Tudo em dia'}</div>
        </button>
        <button type="button" className="stat-card" onClick={() => nav('/minhas')} style={{ borderLeftColor: '#16a34a' }}>
          <div className="stat-label"><CheckCircle2 size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Publicadas</div>
          <div className="stat-value">{publicadas}</div>
          <div className="text-xs muted">Concluídas</div>
        </button>
        <button type="button" className="stat-card" onClick={() => nav('/minhas')}>
          <div className="stat-label">Total</div>
          <div className="stat-value">{total}</div>
          <div className="text-xs muted">Soma geral</div>
        </button>
      </div>

      {/* Pendências em destaque — o que a equipe está esperando do secretário */}
      <div id="pendencias" />
      {(painel.pedidos_pendentes.length > 0 || painel.aprovacoes_pendentes.length > 0) && (
        <div className="card" style={{ marginBottom: 18, borderColor: 'var(--c-warning)', borderWidth: 2 }}>
          <div className="card-body" style={{ background: 'var(--c-warning-soft)' }}>
            <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 12 }}>
              <AlertCircle size={20} color="var(--c-warning)" />
              <strong style={{ fontSize: 15 }}>A equipe está esperando você</strong>
            </div>

            {painel.pedidos_pendentes.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div className="text-xs muted strong" style={{ textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                  Pedidos de informação ({painel.pedidos_pendentes.length})
                </div>
                <div className="stack" style={{ gap: 6 }}>
                  {painel.pedidos_pendentes.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="row"
                      onClick={() => nav(`/demandas/${p.demanda_codigo}`)}
                      style={{
                        background: '#fff', border: '1px solid var(--c-border)', borderRadius: 8,
                        padding: '8px 12px', justifyContent: 'space-between', cursor: 'pointer', textAlign: 'left',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{p.titulo}</div>
                        <div className="text-xs muted">
                          {p.demanda_titulo} · {p.itens_pendentes} de {p.itens_total} item{p.itens_total === 1 ? '' : 's'} pendente{p.itens_pendentes === 1 ? '' : 's'} · {format(new Date(p.created_at), "dd 'de' MMM", { locale: ptBR })}
                        </div>
                      </div>
                      <ChevronRight size={16} color="var(--c-text-muted)" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {painel.aprovacoes_pendentes.length > 0 && (
              <div>
                <div className="text-xs muted strong" style={{ textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                  Aprovações pendentes ({painel.aprovacoes_pendentes.length})
                </div>
                <div className="stack" style={{ gap: 6 }}>
                  {painel.aprovacoes_pendentes.map((a) => {
                    const dias = differenceInCalendarDays(new Date(a.expira_em), new Date());
                    return (
                      <a
                        key={a.id}
                        href={`/aprovar/${a.token}`}
                        target="_blank"
                        rel="noreferrer"
                        className="row"
                        style={{
                          background: '#fff', border: '1px solid var(--c-border)', borderRadius: 8,
                          padding: '8px 12px', justifyContent: 'space-between', textDecoration: 'none', color: 'inherit',
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}><FileQuestion size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />{a.titulo}</div>
                          <div className="text-xs muted">
                            {a.demanda_titulo} · {dias <= 0 ? 'expira hoje' : `expira em ${dias} dia${dias === 1 ? '' : 's'}`}
                          </div>
                        </div>
                        <ChevronRight size={16} color="var(--c-text-muted)" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid-2" style={{ gap: 16 }}>
        {/* Próximas datas */}
        <div className="card">
          <div className="card-header">
            <strong><Calendar size={14} style={{ verticalAlign: 'middle' }} /> Próximas datas</strong>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {painel.proximas_datas.length === 0 ? (
              <div className="muted text-sm" style={{ padding: 18, textAlign: 'center' }}>
                Sem datas marcadas nos próximos 21 dias.
              </div>
            ) : (
              <div className="stack" style={{ gap: 0 }}>
                {painel.proximas_datas.map((d) => {
                  const dataRef = d.data_acao || d.data_postagem;
                  const dt = parseDate(dataRef);
                  const acontecendo = acaoAcontecendoAgora(d.data_acao, d.data_acao_fim);
                  const passou = isPast(dt) && !isToday(dt) && !acontecendo;
                  const hoje = isToday(dt) || acontecendo;
                  const cor = passou ? '#dc2626' : hoje ? '#16a34a' : 'var(--c-text-muted)';
                  const dur = duracaoTexto(d.data_acao, d.data_acao_fim);
                  const dataLabel = d.data_acao
                    ? formatPeriodo(d.data_acao, d.data_acao_fim, { short: true })
                    : format(dt, "dd/MM");
                  return (
                    <button
                      key={d.id}
                      type="button"
                      className="row"
                      onClick={() => nav(`/demandas/${d.codigo}`)}
                      style={{
                        padding: '12px 14px', justifyContent: 'space-between', cursor: 'pointer',
                        borderBottom: '1px solid var(--c-border)', textAlign: 'left',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{d.titulo}</div>
                        <div className="row-wrap" style={{ gap: 6 }}>
                          <Badge color={ETAPA_MAP[d.etapa]?.color}>{ETAPA_MAP[d.etapa]?.label}</Badge>
                          <PrioridadeBadge prioridade={d.prioridade} />
                          {acontecendo && <Badge color="success">Acontecendo agora</Badge>}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', fontSize: 12, color: cor, fontWeight: 600 }}>
                        {acontecendo ? 'Hoje' : passou ? 'Atrasada' : dataLabel}
                        <div className="text-xs muted" style={{ fontWeight: 400 }}>
                          {d.data_acao ? (dur ? `ação · ${dur}` : 'ação') : 'postagem'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Recentes */}
        <div className="card">
          <div className="card-header">
            <strong><Clock size={14} style={{ verticalAlign: 'middle' }} /> Movimentação recente</strong>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {painel.recentes.length === 0 ? (
              <div className="muted text-sm" style={{ padding: 18, textAlign: 'center' }}>
                Sem movimentação recente.
              </div>
            ) : (
              <div className="stack" style={{ gap: 0 }}>
                {painel.recentes.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    className="row"
                    onClick={() => nav(`/demandas/${d.codigo}`)}
                    style={{
                      padding: '12px 14px', justifyContent: 'space-between', cursor: 'pointer',
                      borderBottom: '1px solid var(--c-border)', textAlign: 'left',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>
                        {d.titulo}
                        {d.nao_lidas > 0 && <Badge color="primary" style={{ marginLeft: 6 }}>{d.nao_lidas} nova{d.nao_lidas === 1 ? '' : 's'}</Badge>}
                      </div>
                      <div className="row-wrap" style={{ gap: 6 }}>
                        <Badge color={ETAPA_MAP[d.etapa]?.color}>{ETAPA_MAP[d.etapa]?.label}</Badge>
                      </div>
                    </div>
                    <div className="text-xs muted">{format(new Date(d.updated_at), 'dd/MM HH:mm')}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
